/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, OnInit, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './site-bulk-action-modal.component.html';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-site-bulk-action-modal',
  template
})
export class SiteBulkActionModalComponent implements OnInit {
  list: any;

  item: any = {
    name: '{currentName}',
    description: '{currentDescription}',
    siteGroups: [],
    filterTeamsType: 'NONE',
    teams: [],
    filterTeams: [],
    politicalAdvertising: 'ALLOWED'
  };
  form: any = {};
  editing: any = {};
  count = 0;
  canUpdateSite = false;
  canEditAdvertisingAccess = false;
  canEditSiteTeams = false;
  isPoliticalAdsPlatform = false;
  private readonly allNotAxPublisherTeamsState = signal<any[] | null>(null);

  get allNotAxPublisherTeams(): any[] | null { return this.allNotAxPublisherTeamsState(); }
  set allNotAxPublisherTeams(value: any[] | null) { this.allNotAxPublisherTeamsState.set(value); }

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(TeamApiService) private readonly Team: TeamApiService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
  ) {
    this.canUpdateSite = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalUserPermissions.hasAny('MANAGE_PUBLISHING')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.canEditAdvertisingAccess = LocalUserPermissions.hasNetwork('MANAGE_BUSINESS')
      || LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.canEditSiteTeams = this.canEditAdvertisingAccess
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN');
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
  }

  ngOnInit(): void {
    this.count = _.keys(this.list?.bulkSelection || {}).length;
    firstValueFrom(this.Team.listOptions(resourceOptionParams({minimal: true}))).then((page: any) => {
      this.allNotAxPublisherTeams = _.filter(page.results, (team: any) => {
        return team.type !== 'AX_PUBLISHER' && team.type !== 'CREATIVE_AGENCY';
      });
    });
  }

  get politicalAdvertisingBlocked(): boolean {
    return this.item.politicalAdvertising === 'BLOCKED';
  }

  set politicalAdvertisingBlocked(blocked: boolean) {
    this.item.politicalAdvertising = blocked ? 'BLOCKED' : 'ALLOWED';
  }

  isTeamSelected(teamId: string): boolean {
    return (this.item.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    if (!this.canEditSiteTeams || !this.editing.advertisingAccess) {
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.item.teams || [])];
    const index = teams.indexOf(teamId);
    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }
    this.item.teams = teams;
  }

  apply(): void {
    const sites = _.map(this.list.bulkSelection, (v: any, k: string) => {
      const obj: any = {id: k};

      if (this.editing.name) {
        obj.name = this.item.name.replace('{currentName}', v.name);
      }
      if (this.editing.description) {
        obj.description = this.item.description.replace('{currentDescription}', v.description || '') || null;
      }
      if (this.editing.country) {
        obj.country = this.item.country || null;
      }
      if (this.editing.siteGroups) {
        obj.siteGroups = _.map(this.item.siteGroups || [], (sg: any) => {
          return {id: sg.id, name: sg.name};
        });
      }
      if (this.editing.advertisingAccess) {
        obj.filterTeamsType = this.item.filterTeamsType;
        if (this.item.filterTeamsType === 'NONE') {
          obj.teams = this.item.teams || [];
        }
        if (this.item.filterTeamsType === 'EXCLUDES') {
          obj.filterTeams = _.map(this.item.filterTeams || [], (team: any) => {
            return {id: team.id, name: team.name};
          });
        }
      }
      if (this.editing.politicalAdvertising) {
        obj.politicalAdvertising = this.item.politicalAdvertising;
      }

      return obj;
    });

    this.list.miscBulkAction(
      {close: () => this.bsModalRef.hide()},
      this,
      sites
    );
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
